import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import PageMeta from '../../components/common/PageMeta';
import SecondaryButton from '../../components/common/SecondaryButton';
import GAService from '../../services/GAService';
import EarlyAccessForm from './EarlyAccessForm';
import { renderRich } from '../../components/pricing/richText';
import { getCurrentUser, onSessionChange, getLoginURL, rememberReturnTo, startProCheckout } from '../../common/auth';
import { appLink } from '../../common/appLinks';
import { EARLY_ACCESS_FORM_URL, RECAPTCHA_SITE_KEY } from '../../common/constants';
import { readParams } from '../../common/earlyAccess';

const HEADING_SX = { color: 'surface.dark', fontSize: { xs: '2rem', md: '2.75rem' }, mb: 2 };

// /early-access (ocl_online#170): where older "Request early access" links
// land — the Mapper's limit dialog (ocl_issues#2773), emails and blog posts —
// and /pricing's Team card. Since Nov 1 it offers Pro's checkout first, the
// same way as every Pro button (ocl_online#325), and keeps the form for Team,
// which OCL sets up by hand for now. Requests go to Brevo; see
// docs/superpowers/specs/2026-09-24-early-access-form-design.md.
const EarlyAccess = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { source } = readParams(searchParams);
  // undefined = not checked yet, null = checked and signed out. Read after
  // mount, never during render (docs/SEO-AUDIT.md bug #8) — starting at null
  // instead would show the sign-in prompt on the first paint for a
  // signed-in visitor too, then hide it once the effect runs: a flash
  // confirmed on every load (final-review.md M6).
  const [user, setUser] = React.useState(undefined);
  const [sent, setSent] = React.useState(null);
  const heading = React.useRef(null);

  React.useEffect(() => {
    const update = () => setUser(getCurrentUser() || null);
    update();
    return onSessionChange(update);
  }, []);

  React.useEffect(() => {
    if (sent && heading.current)
      heading.current.focus();
  }, [sent]);

  // appLink reads window.location and localStorage, so resolve the Mapper
  // link here, in the event, not during render (docs/SEO-AUDIT.md bug #8).
  // The form's success payload is just { email }.
  const onSuccess = ({ email }) => {
    GAService.recordLead({ source });
    setSent({ email, mapperURL: appLink('mapper') });
  };

  // Same pair UserMenu uses: come back to this exact URL, query string
  // included, after the SSO round trip.
  const signIn = () => {
    rememberReturnTo();
    getLoginURL().then(url => { window.location.href = url; });
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 5, md: 8 } }}>
      <PageMeta title={t('early_access.title')} robots="noindex, follow" />
      {sent ? (
        <Box>
          <Typography ref={heading} tabIndex={-1} variant="h2" component="h1" sx={{ ...HEADING_SX, outline: 'none' }}>
            {t('early_access.success_title')}
          </Typography>
          <Typography sx={{ color: 'surface.contrastText', mb: 3 }}>
            {t('early_access.success_text', { email: sent.email })}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Button href={sent.mapperURL} variant="contained" disableElevation>{t('early_access.next_mapper')}</Button>
            {/* /newsletter is an nginx redirect to Brevo, not an app route: a real page load. */}
            <SecondaryButton href="/newsletter">{t('early_access.next_newsletter')}</SecondaryButton>
          </Box>
        </Box>
      ) : (
        <Box>
          <Typography variant="h2" component="h1" sx={HEADING_SX}>
            {t('early_access.title')}
          </Typography>
          <Typography sx={{ color: 'surface.contrastText', mb: 2 }}>
            {t('early_access.intro')}
          </Typography>
          <Button href="/pricing" onClick={startProCheckout} variant="contained" disableElevation>
            {t('pricing.plans.pro.cta')}
          </Button>
          <Typography variant="body2" sx={{ color: 'surface.contrastText', mt: 1.5, mb: 4 }}>
            {renderRich(t('pricing.plans.pro.terms'))}
          </Typography>
          <Typography sx={{ color: 'surface.contrastText', mb: 2 }}>
            {renderRich(t('early_access.team_intro'))}
          </Typography>
          {/* Signed in: nothing shown here — the form's own prefilled,
              editable OCL username field is the signal that the request
              will be linked to the account. user === null (not just falsy):
              undefined means "not checked yet", and showing the prompt then
              would flash it for signed-in visitors too. */}
          {user === null && (
            <Typography variant="body2" sx={{ color: 'surface.contrastText', mb: 4 }}>
              {t('early_access.sign_in_prompt')}{' '}
              <Link component="button" type="button" onClick={signIn} sx={{ verticalAlign: 'baseline' }}>
                {t('early_access.sign_in_link')}
              </Link>
            </Typography>
          )}
          <EarlyAccessForm
            formUrl={EARLY_ACCESS_FORM_URL}
            siteKey={RECAPTCHA_SITE_KEY}
            source={source}
            user={user}
            onSuccess={onSuccess}
          />
        </Box>
      )}
    </Container>
  );
};

export default EarlyAccess;
